import { useCallback, useEffect, useRef, useState } from "react";
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent,
    LinearProgress, Tab, Tabs, Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { useSnackbar } from "notistack";
import { apiConstants, getGeneric, postGeneric } from "../../common/constants";
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from "../../redux/commonApiCalls";
import { readFileAsRows, downloadSampleSheet } from "./bulkRunner";
import { MUTED, NAVY, gradientBtnSx } from "./SectionCard";
import { DIALOG_PAPER_SX, TicketsDialogTitle, pillBtnSx } from "./TicketsUi";
import { ACCENTS, COLORS } from "./emsTheme";
import { emailListProblem, emailProblem } from "./emailValidation";

const SAMPLE_ROWS = [
    {
        merchant: "Staging Test", category: "NDR", subcategory: "Order not getting Re-attempted",
        awb_number: "14408941194623", description: "Customer available, please reattempt", severity: "P1",
        show_on_merchant_panel: "yes", email_updates_to: "ops@merchant.com", cc_emails: "kam@prozo.com",
        attachment_links: "https://example.com/pod-photo.jpg",
    },
    {
        merchant: "Staging Test", category: "Billing and Invoice", subcategory: "Invoice not received",
        awb_number: "", description: "", severity: "", show_on_merchant_panel: "no", email_updates_to: "", cc_emails: "",
        attachment_links: "",
        invoiceCycle: "2026-09-01 to 2026-09-30",
    },
];

// Every other column is passed through as that subcategory's own field (e.g. invoiceCycle).
const KNOWN_COLUMNS = ["merchant", "category", "subcategory", "awb_number", "description", "severity",
    "show_on_merchant_panel", "email_updates_to", "cc_emails", "attachment_links"];
const YES = ["yes", "y", "true", "1"];
const text = (v) => (v == null ? "" : v.toString().trim());

const JOBS = `${apiConstants.ESCALATION_DETAIL_V2}/bulk-create/jobs`;
const STORAGE_KEY = "ems_bulk_create_job";
const FINISHED = ["DONE", "CANCELLED", "INTERRUPTED", "FAILED"];
const POLL_MS = 2000;

const remember = (id) => { try { id ? localStorage.setItem(STORAGE_KEY, id) : localStorage.removeItem(STORAGE_KEY); } catch (e) { /* storage off */ } };
const remembered = () => { try { return localStorage.getItem(STORAGE_KEY); } catch (e) { return null; } };

// Status chip: label + colours.
const STATUS_META = {
    QUEUED: { label: "Starting", bg: "#EEF2F7", color: "#41556B" },
    RUNNING: { label: "In progress", bg: "#F1ECF7", color: "#663F95" },
    DONE: { label: "Finished", bg: "#E8F5E9", color: "#2E7D32" },
    CANCELLED: { label: "Stopped", bg: "#FFF4E5", color: "#B45309" },
    INTERRUPTED: { label: "Interrupted", bg: "#FFF4E5", color: "#B45309" },
    FAILED: { label: "Stopped by an error", bg: "#FDECEA", color: "#C0392B" },
};
const STATUS_NOTE = {
    CANCELLED: "You stopped the upload. Rows without a result weren't created.",
    INTERRUPTED: "The server restarted during the upload. Rows without a result weren't created — upload those again.",
    FAILED: "The upload stopped because of an error. Rows without a result weren't created.",
};

const COLUMN_GUIDE = [
    { title: "Required", cols: ["merchant", "category", "subcategory"] },
    { title: "Optional", cols: ["awb_number", "description", "severity", "show_on_merchant_panel", "email_updates_to", "cc_emails", "attachment_links"] },
];

/**
 * Bulk Create — one ticket per sheet row. Rows are checked here first (merchant, AWB, links, …);
 * the valid ones go to the server as one background job (POST /escalation/bulk-create/jobs),
 * which creates them in batches and records progress. This dialog polls the job for the progress
 * bar; closing it doesn't stop the upload — reopening Bulk Create picks the job back up.
 */
const BulkCreateModal = ({ open, onClose, onDone }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery();
    const fileInputRef = useRef(null);
    const pollRef = useRef(null);
    // Bumped on every stop, so a job GET still in flight when the dialog closes (or the page
    // unmounts) drops its answer instead of painting it or arming the next poll.
    const pollGenRef = useRef(0);
    const openRef = useRef(open);
    const sheetRef = useRef({}); // rowNumber -> original sheet row, for "Download failed rows"
    const localFailCountRef = useRef(0); // rows rejected in the browser, for the finish message
    const [fileName, setFileName] = useState("");
    const [checking, setChecking] = useState(false);
    const [localFailures, setLocalFailures] = useState([]);
    const [job, setJob] = useState(null);
    const [tab, setTab] = useState("FAILED");
    const [showGuide, setShowGuide] = useState(false);

    const running = !!job && !FINISHED.includes(job.status);

    const stopPolling = () => {
        pollGenRef.current += 1;
        if (pollRef.current) clearTimeout(pollRef.current);
        pollRef.current = null;
    };

    const poll = useCallback(async (jobId) => {
        stopPolling();
        // Closed (or gone) before this started, e.g. while the upload was being sent: reopening
        // Bulk Create picks the job up again.
        if (!openRef.current) return;
        const gen = pollGenRef.current;
        try {
            const res = await getGeneric(`${JOBS}/${jobId}`);
            if (gen !== pollGenRef.current) return;
            const next = res?.data;
            setJob(next);
            if (next && !FINISHED.includes(next.status)) {
                pollRef.current = setTimeout(() => poll(jobId), POLL_MS);
            } else if (next) {
                const rows = next.total + localFailCountRef.current;
                enqueueSnackbar(`Bulk create finished — ${next.successCount} of ${rows} tickets created.`,
                    { variant: next.successCount === rows && next.status === "DONE" ? "success" : "warning" });
                onDone?.();
            }
        } catch (e) {
            if (gen !== pollGenRef.current) return;
            remember(null);
            setJob(null);
        }
    }, [enqueueSnackbar, onDone]);

    // Reopening the dialog picks up an upload that's still running (or just finished).
    useEffect(() => {
        openRef.current = open;
        if (!open) { stopPolling(); return undefined; }
        const id = remembered();
        if (id && !job) poll(id);
        return () => { openRef.current = false; stopPolling(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const reset = () => {
        stopPolling();
        remember(null);
        sheetRef.current = {};
        localFailCountRef.current = 0;
        setFileName("");
        setLocalFailures([]);
        setJob(null);
        setTab("FAILED");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleClose = () => {
        // A running upload keeps going on the server; only forget a finished one.
        if (!running) reset();
        else stopPolling();
        onClose();
    };

    const resolveMerchant = (value) => {
        const v = text(value).toLowerCase();
        if (!v) return { error: "Merchant is missing" };
        const byId = merchants.find((m) => m.id === text(value));
        if (byId) return { id: byId.id };
        const byName = merchants.filter((m) => (m.name || "").trim().toLowerCase() === v);
        if (byName.length === 1) return { id: byName[0].id };
        return { error: byName.length > 1 ? `More than one merchant is named "${text(value)}" — use its id` : `Merchant "${text(value)}" not found` };
    };

    const checkRow = (row) => {
        const merchant = resolveMerchant(row.merchant);
        if (merchant.error) return { error: merchant.error };
        if (!text(row.category) || !text(row.subcategory)) return { error: "Category or subcategory is missing" };
        const awb = text(row.awb_number);
        if (/[,\s]/.test(awb)) return { error: "Only one AWB per ticket — put each AWB on its own row" };
        const links = text(row.attachment_links).split(",").map((l) => l.trim()).filter(Boolean);
        if (links.some((l) => !/^https:\/\//i.test(l))) return { error: "Attachment links must start with https://" };
        if (links.length > 10) return { error: "At most 10 attachment links per ticket" };
        const severity = text(row.severity).toUpperCase();
        if (severity && !["P0", "P1", "P2"].includes(severity)) return { error: `Severity must be P0, P1 or P2 (got "${row.severity}")` };
        const visible = YES.includes(text(row.show_on_merchant_panel).toLowerCase());
        if (visible) {
            const emailIssue = (text(row.email_updates_to) && emailProblem(text(row.email_updates_to))) || emailListProblem(text(row.cc_emails));
            if (emailIssue) return { error: emailIssue };
        }
        const customFieldValues = {};
        Object.keys(row).filter((k) => !KNOWN_COLUMNS.includes(k) && text(row[k]))
            .forEach((k) => { customFieldValues[k] = text(row[k]); });
        return {
            item: {
                merchantId: merchant.id,
                category: text(row.category),
                subCategory: text(row.subcategory),
                awbNumber: awb || undefined,
                description: text(row.description) || undefined,
                severity: severity || undefined,
                visibleToMerchant: visible,
                requesterEmail: visible ? text(row.email_updates_to) || undefined : undefined,
                additionalEmails: visible ? text(row.cc_emails).split(",").map((e) => e.trim()).filter(Boolean) : [],
                customFieldValues,
                attachmentLinks: links,
            },
        };
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        reset();
        setFileName(file.name);
        setChecking(true);
        try {
            const sheet = await readFileAsRows(file);
            if (!sheet.length) {
                enqueueSnackbar("No rows found in Sheet1.", { variant: "warning" });
                return;
            }
            const failures = [];
            const valid = [];
            sheet.forEach((row, i) => {
                const rowNumber = i + 2; // header is row 1
                sheetRef.current[rowNumber] = row;
                const label = text(row.awb_number);
                const checked = checkRow(row);
                if (checked.error) failures.push({ rowNumber, label, status: "FAILED", message: checked.error });
                else valid.push({ rowNumber, label, item: checked.item });
            });
            setLocalFailures(failures);
            localFailCountRef.current = failures.length;
            if (!valid.length) {
                enqueueSnackbar("None of the rows can be created — see the reasons below.", { variant: "warning" });
                return;
            }
            const res = await postGeneric(JOBS, { rows: valid });
            remember(res.data.id);
            setJob(res.data);
            poll(res.data.id);
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || err.message || "Bulk create couldn't start.", { variant: "error" });
        } finally {
            setChecking(false);
        }
    };

    const stop = async () => {
        try {
            await postGeneric(`${JOBS}/${job.id}/cancel`, {});
            enqueueSnackbar("Stopping after the rows in progress…", { variant: "info" });
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || "Couldn't stop the upload", { variant: "error" });
        }
    };

    const all = [...localFailures, ...((job && job.results) || [])].sort((a, b) => a.rowNumber - b.rowNumber);
    const failedRows = all.filter((r) => r.status !== "SUCCESS");
    const createdRows = all.filter((r) => r.status === "SUCCESS");
    const total = (job?.total || 0) + localFailures.length;
    const done = (job?.processed || 0) + localFailures.length;
    const pct = total ? Math.round((done * 100) / total) : 0;
    const started = !!job || localFailures.length > 0;
    const statusMeta = job ? STATUS_META[job.status] : (localFailures.length ? STATUS_META.DONE : null);
    const shown = tab === "FAILED" ? failedRows : createdRows;

    const downloadFailed = () => {
        const rows = failedRows.map((r) => ({ ...(sheetRef.current[r.rowNumber] || { awb_number: r.label }), sheet_row: r.rowNumber, reason: r.message }));
        downloadSampleSheet(rows, "Bulk_Create_Failed_Rows.xlsx");
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: DIALOG_PAPER_SX }}>
            <TicketsDialogTitle icon={PostAddIcon} title="Bulk Create Tickets" sub="Raise many tickets from a sheet" onClose={handleClose} />
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>

                {/* Step 1 — the sheet */}
                <Box sx={{ border: "1px solid #E7EAF0", borderRadius: "12px", p: 2 }}>
                    <Typography sx={{ fontSize: 13, color: NAVY, fontWeight: 600 }}>
                        One ticket per row, in <b>Sheet1</b> of a CSV or Excel file.
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>
                        Up to 5,000 rows · file up to 2 MB · attachments as https links, 20 MB per ticket
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5, alignItems: "center" }}>
                        <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={running || checking}
                            sx={pillBtnSx}>
                            {fileName || "Choose file"}
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                accept=".csv,.xlsx,.xls"
                                onChange={(e) => {
                                    if (e.target.files?.[0]?.size > 2 * 1024 * 1024) {
                                        enqueueSnackbar("File is larger than the 2 MB limit.", { variant: "error" });
                                        e.target.value = "";
                                        return;
                                    }
                                    handleFile(e);
                                }}
                            />
                        </Button>
                        <Button variant="text" startIcon={<GetAppIcon />} sx={{ textTransform: "none", fontWeight: 700, color: COLORS.purple, borderRadius: "999px", "&:hover": { bgcolor: COLORS.purpleSoft } }}
                            onClick={() => downloadSampleSheet(SAMPLE_ROWS, "Bulk_Create_Sample.xlsx")}>
                            Download sample
                        </Button>
                        <Button variant="text" endIcon={<ExpandMoreIcon sx={{ transform: showGuide ? "rotate(180deg)" : "none", transition: "transform .2s" }} />}
                            sx={{ textTransform: "none", ml: "auto", color: MUTED }} onClick={() => setShowGuide((v) => !v)}>
                            Column guide
                        </Button>
                    </Box>
                    {showGuide && (
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #F1F3F6", display: "flex", flexDirection: "column", gap: 1 }}>
                            {COLUMN_GUIDE.map((g) => (
                                <Box key={g.title} sx={{ display: "flex", gap: 1, alignItems: "baseline", flexWrap: "wrap" }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: NAVY, width: 64 }}>{g.title}</Typography>
                                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", flex: 1 }}>
                                        {g.cols.map((c) => (
                                            <Box key={c} component="code" sx={{ fontSize: 12, bgcolor: "#F4F6F9", color: "#344563", px: 0.75, py: 0.25, borderRadius: "6px" }}>{c}</Box>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                            <Typography component="ul" sx={{ fontSize: 12, color: MUTED, m: 0, pl: 2.25, "& li": { mb: 0.25 } }}>
                                <li><b>merchant</b>: name as shown in the panel, or its id.</li>
                                <li><b>awb_number</b>: one AWB per row. <b>severity</b>: P0, P1 or P2 (blank = the subcategory&apos;s default).</li>
                                <li><b>show_on_merchant_panel</b>: yes/no. When yes, <b>email_updates_to</b> is required; <b>cc_emails</b> optional.</li>
                                <li><b>attachment_links</b>: comma-separated https links — Google Drive / Docs / Sheets and Dropbox share links work if shared as &quot;Anyone with the link&quot;.</li>
                                <li>Any other column is the subcategory&apos;s own field, e.g. <b>invoiceCycle</b>.</li>
                            </Typography>
                        </Box>
                    )}
                </Box>

                {checking && (
                    <Box>
                        <LinearProgress sx={{ height: 4, borderRadius: 4, bgcolor: COLORS.purpleSoft, "& .MuiLinearProgress-bar": { background: ACCENTS.purple.grad } }} />
                        <Typography sx={{ fontSize: 13, color: MUTED, mt: 1 }}>Checking the sheet…</Typography>
                    </Box>
                )}

                {/* Step 2 — progress */}
                {started && (
                    <Box sx={{ border: "1px solid #E7EAF0", borderRadius: "12px", p: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.25 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                                {pct}% · {done.toLocaleString("en-IN")} of {total.toLocaleString("en-IN")} rows
                            </Typography>
                            {statusMeta && (
                                <Chip size="small" label={statusMeta.label}
                                    sx={{ bgcolor: statusMeta.bg, color: statusMeta.color, fontWeight: 700, fontSize: 12 }} />
                            )}
                        </Box>
                        <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4, bgcolor: COLORS.purpleSoft, "& .MuiLinearProgress-bar": { background: ACCENTS.purple.grad, borderRadius: 4 } }} />
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mt: 1.5 }}>
                            {[
                                { label: "Created", value: createdRows.length, color: "#2E7D32", bg: "#E8F5E9" },
                                { label: "Failed", value: failedRows.length, color: "#C0392B", bg: "#FDECEA" },
                                { label: "Remaining", value: Math.max(0, total - done), color: "#41556B", bg: "#F4F6F9" },
                            ].map((t) => (
                                <Box key={t.label} sx={{ bgcolor: t.bg, borderRadius: "10px", px: 1.5, py: 1 }}>
                                    <Typography sx={{ fontSize: 12, color: t.color, fontWeight: 600 }}>{t.label}</Typography>
                                    <Typography sx={{ fontSize: 18, color: t.color, fontWeight: 700 }}>{t.value.toLocaleString("en-IN")}</Typography>
                                </Box>
                            ))}
                        </Box>
                        {job && STATUS_NOTE[job.status] && (
                            <Typography sx={{ fontSize: 12, color: "#B45309", mt: 1.25 }}>{STATUS_NOTE[job.status]}</Typography>
                        )}
                        {running && (
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.25, gap: 1 }}>
                                <Typography sx={{ fontSize: 12, color: MUTED }}>
                                    Runs on the server — you can close this window and reopen Bulk Create to check on it.
                                </Typography>
                                <Button size="small" color="error" onClick={stop} disabled={job?.cancelRequested} sx={{ textTransform: "none", flexShrink: 0 }}>
                                    {job?.cancelRequested ? "Stopping…" : "Stop upload"}
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Step 3 — results */}
                {all.length > 0 && (
                    <Box sx={{ border: "1px solid #E7EAF0", borderRadius: "12px", overflow: "hidden" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #E7EAF0", pr: 1 }}>
                            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600, fontSize: 13 },
                                "& .MuiTab-root.Mui-selected": { color: COLORS.purple }, "& .MuiTabs-indicator": { background: ACCENTS.purple.grad } }}>
                                <Tab value="FAILED" label={`Failed (${failedRows.length})`} />
                                <Tab value="SUCCESS" label={`Created (${createdRows.length})`} />
                            </Tabs>
                            {tab === "FAILED" && failedRows.length > 0 && (
                                <Button size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={downloadFailed} sx={{ textTransform: "none", fontWeight: 700, color: COLORS.purple }}>
                                    Download failed rows
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
                            {shown.length === 0 ? (
                                <Typography sx={{ fontSize: 13, color: MUTED, p: 2, textAlign: "center" }}>
                                    {tab === "FAILED" ? "No failed rows." : "No tickets created yet."}
                                </Typography>
                            ) : (
                                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13,
                                    "& th": { textAlign: "left", color: MUTED, fontWeight: 600, fontSize: 12, px: 1.5, py: 0.75, bgcolor: "#F9FAFB", position: "sticky", top: 0 },
                                    "& td": { px: 1.5, py: 0.75, borderTop: "1px solid #F1F3F6", verticalAlign: "top" } }}>
                                    <thead>
                                        <tr><th style={{ width: 56 }}>Row</th><th style={{ width: 130 }}>AWB</th><th>{tab === "FAILED" ? "Reason" : "Ticket"}</th></tr>
                                    </thead>
                                    <tbody>
                                        {shown.map((r) => (
                                            <tr key={r.rowNumber}>
                                                <td style={{ color: MUTED }}>{r.rowNumber}</td>
                                                <td>{awbOf(r) || "—"}</td>
                                                <td style={{ color: tab === "FAILED" ? "#C0392B" : "#2E7D32", fontWeight: tab === "FAILED" ? 400 : 600 }}>
                                                    {tab === "FAILED" ? r.message : r.escalationNumber}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {!running && started && (
                    <Button onClick={reset} sx={{ textTransform: "none", fontWeight: 700, color: COLORS.purple, borderRadius: "999px" }}>Upload another sheet</Button>
                )}
                <LoadingButton variant="contained" onClick={handleClose} sx={gradientBtnSx}>
                    {running ? "Close — keeps running" : "Done"}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

// Older jobs labelled rows "Row 4 · AWB"; new ones carry just the AWB.
const awbOf = (r) => (r.label || "").replace(/^Row \d+( · )?/, "");

export default BulkCreateModal;
